import type { ListChannelsResponse } from '@qlk/shared';
import { useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { api, type LoiApi } from '../lib/api.js';
import { Avatar } from '../ui/Avatar.js';

/**
 * Ô tìm kiếm chung của thanh trên, mở bằng **Ctrl+K**.
 *
 * Bước 1/3 chỉ tìm **kênh theo tên** — dùng đúng `GET /api/v1/channels?q=` mà
 * bảng Quản lý kênh đã dùng, không thêm API. Placeholder vẫn ghi "video,
 * người…" vì đó là đích của bước sau; ô kết quả nói rõ phạm vi hiện tại để
 * không ai gõ tên người rồi tưởng app hỏng.
 */

const TRE_GO = 250;

export function TimKiemChung() {
  const [mo, setMo] = useState(false);
  const [tu, setTu] = useState('');
  const [tuTre, setTuTre] = useState('');
  const dieuHuong = useNavigate();
  const oNhap = useRef<HTMLInputElement>(null);

  /* Ctrl+K / Cmd+K mở hộp, Esc đóng. */
  useEffect(() => {
    const xuLy = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setMo(true);
      }
      if (e.key === 'Escape') setMo(false);
    };
    window.addEventListener('keydown', xuLy);
    return () => {
      window.removeEventListener('keydown', xuLy);
    };
  }, []);

  useEffect(() => {
    if (mo) oNhap.current?.focus();
  }, [mo]);

  /* Chờ người dùng gõ xong mới gọi — mỗi ký tự một lượt là vô ích và tốn. */
  useEffect(() => {
    const h = setTimeout(() => {
      setTuTre(tu.trim());
    }, TRE_GO);
    return () => {
      clearTimeout(h);
    };
  }, [tu]);

  const ketQua = useQuery<ListChannelsResponse, LoiApi>({
    queryKey: ['channels', 'tim-nhanh', tuTre] as const,
    queryFn: () =>
      api.get<ListChannelsResponse>(
        `/api/v1/channels?q=${encodeURIComponent(tuTre)}&limit=8&tab=all&sort=name&period=28d`,
      ),
    enabled: mo && tuTre.length >= 2,
  });

  const kenh = ketQua.data?.items ?? [];

  const chon = (id: string) => {
    setMo(false);
    setTu('');
    void dieuHuong(`/kenh/${id}`);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setMo(true);
        }}
        className="vung-bam flex w-full items-center gap-2 rounded-o border border-vien bg-be-mat px-3 text-left text-chu-phu hover:bg-neutral-nen md:w-[320px]"
      >
        <Search size={16} aria-hidden="true" className="shrink-0" />
        <span className="truncate text-co-ban">Tìm kênh, video, người…</span>
        <kbd className="ml-auto hidden shrink-0 rounded border border-vien bg-neutral-nen px-1.5 py-0.5 text-phu font-medium text-chu-phu md:inline">
          Ctrl K
        </kbd>
      </button>

      {mo && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-[12vh]">
          <button
            type="button"
            aria-label="Đóng tìm kiếm"
            onClick={() => {
              setMo(false);
            }}
            className="absolute inset-0 bg-chu/40"
          />

          <div
            role="dialog"
            aria-label="Tìm kiếm"
            aria-modal="true"
            className="relative w-full max-w-[560px] overflow-hidden rounded-the border border-vien bg-be-mat shadow-xl"
          >
            <div className="flex items-center gap-2 border-b border-vien px-4">
              <Search size={18} aria-hidden="true" className="shrink-0 text-chu-phu" />
              <input
                ref={oNhap}
                value={tu}
                onChange={(e) => {
                  setTu(e.target.value);
                }}
                placeholder="Tìm kênh, video, người… Ctrl K"
                aria-label="Từ khoá tìm kiếm"
                className="h-12 flex-1 bg-transparent text-co-ban text-chu outline-none placeholder:text-chu-phu"
              />
              <button
                type="button"
                aria-label="Đóng tìm kiếm"
                onClick={() => {
                  setMo(false);
                }}
                className="inline-flex size-8 items-center justify-center rounded-o text-chu-phu hover:bg-neutral-nen"
              >
                <X size={16} aria-hidden="true" />
              </button>
            </div>

            <div className="max-h-[50vh] overflow-y-auto p-2">
              {tuTre.length < 2 ? (
                <p className="px-3 py-4 text-phu text-chu-phu">
                  Gõ ít nhất 2 ký tự. Bước này tìm <strong>kênh theo tên</strong>; video và người sẽ
                  thêm ở bước sau.
                </p>
              ) : ketQua.isPending ? (
                <p className="px-3 py-4 text-phu text-chu-phu">Đang tìm…</p>
              ) : kenh.length === 0 ? (
                <p className="px-3 py-4 text-phu text-chu-phu">Không có kênh nào khớp “{tuTre}”.</p>
              ) : (
                <ul className="flex flex-col">
                  {kenh.map((k) => (
                    <li key={k.id}>
                      <button
                        type="button"
                        onClick={() => {
                          chon(k.id);
                        }}
                        className="flex w-full items-center gap-3 rounded-o px-3 py-2 text-left hover:bg-neutral-nen"
                      >
                        <Avatar name={k.name} color={k.avatarColor} size={28} />
                        <span className="min-w-0">
                          <span className="block truncate text-co-ban text-chu">{k.name}</span>
                          <span className="block truncate text-phu text-chu-phu">{k.handle}</span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
